// Sale-applicable tax codes (SALES + EXEMPT) offered when declaring sale
// categories at onboarding. This mirrors seed/default_tax_codes.json — the
// authoring source of truth — but lives here because onboarding runs before the
// business (and its real tax codes) exist, so the dropdown can't fetch them.
//
// The backend validates every submitted code against the business's actual
// seeded codes, so any drift here fails safe: a missing option, or a stale
// option rejected at create — never a mis-saved rate. Keep in sync with the
// seed when codes change.

export type SaleTaxFlag = 'tax_registered_sales_tax' | 'tax_registered_goods_tax' | null;

export interface SaleTaxOption {
  /** tax_codes.code; null is represented by the always-present "No tax" choice. */
  code: string;
  label: string;
  group: 'Sales Tax' | 'Goods Tax' | 'Exempt';
  /** Registration flag gating availability; null = always available. */
  flag: SaleTaxFlag;
}

export const SALE_TAX_OPTIONS: SaleTaxOption[] = [
  // Sales Tax — percentage (10%)
  { code: 'ST', label: 'Sales Tax — general (10%)', group: 'Sales Tax', flag: 'tax_registered_sales_tax' },
  { code: 'ST-TELCO', label: 'Telecommunications (10%)', group: 'Sales Tax', flag: 'tax_registered_sales_tax' },
  { code: 'ST-RESTAURANT', label: 'Restaurant services (10%)', group: 'Sales Tax', flag: 'tax_registered_sales_tax' },
  { code: 'ST-TRAVEL-SEA', label: 'Domestic sea travel (10% of fare)', group: 'Sales Tax', flag: 'tax_registered_sales_tax' },
  { code: 'ST-ALS', label: 'Accounting, legal & security (10%)', group: 'Sales Tax', flag: 'tax_registered_sales_tax' },
  { code: 'ST-MVH', label: 'Motor vehicle rental & hire (10%)', group: 'Sales Tax', flag: 'tax_registered_sales_tax' },
  { code: 'ST-REALESTATE', label: 'Real estate agency services (10%)', group: 'Sales Tax', flag: 'tax_registered_sales_tax' },
  { code: 'ST-VIDEO-EQUIP', label: 'Video equipment hire (10%)', group: 'Sales Tax', flag: 'tax_registered_sales_tax' },
  { code: 'ST-COMPUTER', label: 'Computer equipment hire (10%)', group: 'Sales Tax', flag: 'tax_registered_sales_tax' },
  { code: 'ST-VESSEL', label: 'Vessel & aircraft hire/lease (10%)', group: 'Sales Tax', flag: 'tax_registered_sales_tax' },
  { code: 'ST-PLANT', label: 'Plant, machinery & equipment hire (10%)', group: 'Sales Tax', flag: 'tax_registered_sales_tax' },
  { code: 'ST-ELECTRONIC', label: 'Electronic repairs & equipment (10%)', group: 'Sales Tax', flag: 'tax_registered_sales_tax' },
  { code: 'ST-BEAUTY', label: 'Hairdressers, barbers & beauty (10%)', group: 'Sales Tax', flag: 'tax_registered_sales_tax' },
  { code: 'ST-LAUNDRY', label: 'Laundry services (10%)', group: 'Sales Tax', flag: 'tax_registered_sales_tax' },
  { code: 'ST-TYRE', label: 'Tyre services (10%)', group: 'Sales Tax', flag: 'tax_registered_sales_tax' },
  { code: 'ST-PROFESSIONAL', label: 'Professional services (10%)', group: 'Sales Tax', flag: 'tax_registered_sales_tax' },
  // Sales Tax — per-unit (specific)
  { code: 'ST-TRAVEL-OS', label: 'Overseas travel tickets ($10.00/ticket)', group: 'Sales Tax', flag: 'tax_registered_sales_tax' },
  { code: 'ST-TRAVEL-AIR', label: 'Domestic air travel tickets ($10.00/ticket)', group: 'Sales Tax', flag: 'tax_registered_sales_tax' },
  { code: 'ST-VIDEO-HIRE', label: 'Video hire ($2.00/tape)', group: 'Sales Tax', flag: 'tax_registered_sales_tax' },
  { code: 'ST-MOVIE', label: 'Movie tickets ($0.50/ticket)', group: 'Sales Tax', flag: 'tax_registered_sales_tax' },
  { code: 'ST-PETROL', label: 'Petroleum ($0.10/litre)', group: 'Sales Tax', flag: 'tax_registered_sales_tax' },
  { code: 'ST-DIESEL', label: 'Diesel ($0.10/litre)', group: 'Sales Tax', flag: 'tax_registered_sales_tax' },
  { code: 'ST-CASINO', label: 'Casino entrance ($5.00/entry)', group: 'Sales Tax', flag: 'tax_registered_sales_tax' },
  // Goods Tax
  { code: 'GT-IMP', label: 'Goods Tax — Imports (15%)', group: 'Goods Tax', flag: 'tax_registered_goods_tax' },
  { code: 'GT-MFG', label: 'Goods Tax — Local Manufacture (10%)', group: 'Goods Tax', flag: 'tax_registered_goods_tax' },
  // Exempt
  { code: 'EXEMPT', label: 'Exempt (0%)', group: 'Exempt', flag: null },
];

/** The grouped order used for <optgroup> rendering. */
export const SALE_TAX_GROUPS: Array<SaleTaxOption['group']> = ['Sales Tax', 'Goods Tax', 'Exempt'];
